import React, { useState, useMemo, useRef, useEffect } from 'react';
import './Sidebar.css';
import FilterPanel from './FilterPanel';

function Sidebar({ markers, selectedMarkers, onSelectionChange, onMarkerClick, onMarkerUpdate, onFilterChange, onColorChange, onGroupVisibilityChange, sheetConfig, selectionMode, onSelectMode, width, onWidthChange, onClose }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterField, setFilterField] = useState('all');
  const [searchExpanded, setSearchExpanded] = useState(false);
  const [editingMarkerId, setEditingMarkerId] = useState(null);
  const [formData, setFormData] = useState({});
  const [bulkFormData, setBulkFormData] = useState({});
  const [enabledBulkFields, setEnabledBulkFields] = useState({});
  const [filteredByGroup, setFilteredByGroup] = useState(markers); // Markers visible after group filtering
  const [isResizing, setIsResizing] = useState(false);
  const sidebarRef = useRef(null);

  // Handle resize drag
  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e) => {
      if (!onWidthChange) return;
      const newWidth = e.clientX;
      // Constrain width between 250px and 600px
      if (newWidth >= 250 && newWidth <= 600) {
        onWidthChange(newWidth);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, onWidthChange]);

  // Get all unique field names from markers
  const fields = useMemo(() => {
    if (markers.length === 0) return [];
    const fieldSet = new Set();
    markers.forEach(marker => {
      Object.keys(marker).forEach(key => {
        if (!['id', 'latitude', 'longitude', 'rowIndex'].includes(key)) {
          fieldSet.add(key);
        }
      });
    });
    return Array.from(fieldSet);
  }, [markers]);

  // Get viewable fields (only those configured as viewable, or all if not configured)
  const viewableFields = useMemo(() => {
    if (sheetConfig?.viewableColumns && Array.isArray(sheetConfig.viewableColumns)) {
      const viewableSet = new Set(sheetConfig.viewableColumns.map(f => f.toLowerCase()));
      return fields.filter(field => viewableSet.has(field.toLowerCase()));
    }
    // If no viewableColumns config, show all fields
    return fields;
  }, [fields, sheetConfig]);

  // Get editable fields (exclude system fields and locked fields from config)
  const editableFields = useMemo(() => {
    const baseFields = fields.filter(field => !['id', 'rowIndex'].includes(field));

    // If sheetConfig has editableColumns defined, use only those
    if (sheetConfig?.editableColumns && Array.isArray(sheetConfig.editableColumns)) {
      console.log('SheetConfig editableColumns:', sheetConfig.editableColumns);
      console.log('Base fields:', baseFields);

      const editableSet = new Set(sheetConfig.editableColumns.map(f => f.toLowerCase()));
      const filtered = baseFields.filter(field => editableSet.has(field.toLowerCase()));

      console.log('Filtered editable fields:', filtered);
      return filtered;
    }

    // Otherwise, allow all non-system fields
    console.log('No editableColumns config, using all base fields:', baseFields);
    return baseFields;
  }, [fields, sheetConfig]);

  // Filter markers based on search term (applied to group-filtered markers)
  const filteredMarkers = useMemo(() => {
    // If no search term, show all group-filtered markers
    if (!searchTerm.trim()) return filteredByGroup;

    const term = searchTerm.toLowerCase();

    return filteredByGroup.filter(marker => {
      if (filterField === 'all') {
        // Search across all fields
        return Object.entries(marker).some(([key, value]) => {
          if (['id', 'latitude', 'longitude', 'rowIndex'].includes(key)) return false;
          return String(value).toLowerCase().includes(term);
        });
      } else {
        // Search specific field
        return String(marker[filterField] || '').toLowerCase().includes(term);
      }
    });
  }, [filteredByGroup, searchTerm, filterField]);

  // Handle filter changes from FilterPanel
  const handleFilterChange = (visibleMarkers) => {
    setFilteredByGroup(visibleMarkers);
    // Notify App.js so map can also be filtered
    if (onFilterChange) {
      onFilterChange(visibleMarkers);
    }
  };

  // Auto-open edit menu when single marker is selected
  useEffect(() => {
    if (selectedMarkers.size === 1) {
      const selectedId = Array.from(selectedMarkers)[0];
      setEditingMarkerId(selectedId);
      const marker = markers.find(m => m.id === selectedId);
      if (marker) {
        setFormData({ ...marker });
      }
    } else if (selectedMarkers.size === 0) {
      setEditingMarkerId(null);
    }
  }, [selectedMarkers, markers]);

  // Auto-enable all bulk edit fields when multiple markers are selected
  // Also auto-populate fields with common values across all selected markers
  useEffect(() => {
    if (selectedMarkers.size > 1) {
      const allEnabled = {};
      const commonValues = {};

      // Enable all fields
      editableFields.forEach(field => {
        allEnabled[field] = true;
      });

      // Find common values across all selected markers
      const selectedArray = markers.filter(m => selectedMarkers.has(m.id));
      editableFields.forEach(field => {
        const values = selectedArray.map(m => m[field]);
        const firstValue = values[0];

        // Check if all markers have the same value for this field
        const allSame = values.every(val => val === firstValue);

        if (allSame && firstValue !== undefined && firstValue !== null && firstValue !== '') {
          commonValues[field] = firstValue;
        }
      });

      setEnabledBulkFields(allEnabled);
      setBulkFormData(commonValues);
    } else {
      // Reset when not in bulk edit mode
      setEnabledBulkFields({});
      setBulkFormData({});
    }
  }, [selectedMarkers, markers, editableFields]);

  // Get selected markers details
  const selectedMarkersArray = useMemo(() => {
    return markers.filter(m => selectedMarkers.has(m.id));
  }, [markers, selectedMarkers]);

  const handleSelectAll = () => {
    const allIds = new Set(filteredMarkers.map(m => m.id));
    onSelectionChange(allIds);
  };

  const handleDeselectAll = () => {
    onSelectionChange(new Set());
    setEditingMarkerId(null);
  };

  const handleToggleMarker = (markerId) => {
    const newSelection = new Set(selectedMarkers);
    if (newSelection.has(markerId)) {
      newSelection.delete(markerId);
      if (editingMarkerId === markerId) {
        setEditingMarkerId(null);
      }
    } else {
      newSelection.add(markerId);
    }
    onSelectionChange(newSelection);
  };

  const handleMarkerClickInternal = (markerId) => {
    // Single click expands for editing
    setEditingMarkerId(editingMarkerId === markerId ? null : markerId);
    const marker = markers.find(m => m.id === markerId);
    if (marker) {
      setFormData({ ...marker });
    }
    // Also trigger map click
    onMarkerClick(markerId);
  };

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleBulkFieldChange = (field, value) => {
    setBulkFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleToggleBulkField = (field) => {
    setEnabledBulkFields(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handleSaveSingle = (e) => {
    e.preventDefault();
    onMarkerUpdate(editingMarkerId, formData);
    setEditingMarkerId(null);
  };

  const handleApplyBulk = () => {
    // Only include enabled fields in the update
    const updates = {};
    Object.keys(enabledBulkFields).forEach(field => {
      if (enabledBulkFields[field]) {
        updates[field] = bulkFormData[field];
      }
    });

    if (Object.keys(updates).length === 0) {
      alert('Please enable at least one field to update');
      return;
    }

    // Apply to all selected markers
    selectedMarkersArray.forEach(marker => {
      onMarkerUpdate(marker.id, { ...marker, ...updates });
    });

    // Reset bulk form
    setBulkFormData({});
    setEnabledBulkFields({});
  };

  // Get unique values for a field
  const getUniqueValues = (field) => {
    const values = new Set();
    markers.forEach(marker => {
      const val = marker[field];
      if (val !== undefined && val !== null && val !== '') {
        values.add(String(val));
      }
    });
    return Array.from(values).sort();
  };

  // Get a meaningful title for a marker based on available fields
  const getMarkerTitle = (marker) => {
    // First priority: use configured title column if available
    if (sheetConfig?.titleColumn) {
      const titleColumnLower = sheetConfig.titleColumn.toLowerCase();
      const markerField = Object.keys(marker).find(key => key.toLowerCase() === titleColumnLower);
      if (markerField && marker[markerField]) {
        return String(marker[markerField]);
      }
    }

    // Second priority: try common title fields (case-insensitive), but only show if viewable
    const titleFields = ['title', 'name', 'label', 'site', 'location', 'address', 'city'];
    for (const field of titleFields) {
      const markerField = Object.keys(marker).find(key => key.toLowerCase() === field);
      if (markerField && marker[markerField] && viewableFields.includes(markerField)) {
        return String(marker[markerField]);
      }
    }

    // Third priority: use the first viewable non-system field with a value
    const nonSystemFields = viewableFields.filter(
      key => !['id', 'latitude', 'longitude', 'rowIndex', 'color'].includes(key)
    );

    for (const field of nonSystemFields) {
      if (marker[field]) {
        return String(marker[field]);
      }
    }

    // Last resort: use marker ID
    return `Marker ${marker.id}`;
  };

  const renderField = (field, value, onChange) => {
    // Special handling for different field types
    if (field === 'latitude' || field === 'longitude') {
      return (
        <div className="input-with-clear">
          <input
            type="number"
            step="any"
            value={value || ''}
            onChange={(e) => onChange(field, parseFloat(e.target.value) || 0)}
            className="sidebar-input"
          />
          <button
            type="button"
            className="clear-field-btn"
            onClick={() => onChange(field, '')}
            aria-label="Clear field"
          >
            ×
          </button>
        </div>
      );
    }

    if (field === 'color') {
      return (
        <div className="color-input-group">
          <input
            type="color"
            value={value || '#4285F4'}
            onChange={(e) => onChange(field, e.target.value)}
            className="color-picker"
          />
          <div className="input-with-clear">
            <input
              type="text"
              value={value || '#4285F4'}
              onChange={(e) => onChange(field, e.target.value)}
              className="color-text-input"
              placeholder="#4285F4"
            />
            <button
              type="button"
              className="clear-field-btn"
              onClick={() => onChange(field, '#4285F4')}
              aria-label="Clear field"
            >
              ×
            </button>
          </div>
        </div>
      );
    }

    if (field === 'description') {
      return (
        <div className="input-with-clear">
          <textarea
            value={value || ''}
            onChange={(e) => onChange(field, e.target.value)}
            className="sidebar-textarea"
            rows="3"
          />
          <button
            type="button"
            className="clear-field-btn textarea-clear"
            onClick={() => onChange(field, '')}
            aria-label="Clear field"
          >
            ×
          </button>
        </div>
      );
    }

    // Default: text input with autocomplete from existing values
    const uniqueValues = getUniqueValues(field);

    return (
      <div className="input-with-clear">
        <input
          type="text"
          value={value || ''}
          onChange={(e) => onChange(field, e.target.value)}
          className="sidebar-input"
          list={`${field}-datalist`}
          placeholder="Type or select..."
        />
        <datalist id={`${field}-datalist`}>
          {uniqueValues.map(val => (
            <option key={val} value={val} />
          ))}
        </datalist>
        <button
          type="button"
          className="clear-field-btn"
          onClick={() => onChange(field, '')}
          aria-label="Clear field"
        >
          ×
        </button>
      </div>
    );
  };

  return (
    <div
      ref={sidebarRef}
      className="sidebar"
      style={{ width: width ? `${width}px` : undefined }}
    >
      <div
        className="resize-handle"
        onMouseDown={() => setIsResizing(true)}
        title="Drag to resize"
      />
      <div className="sidebar-sheet-handle" aria-hidden="true" />
      <div className="sidebar-header">
        <h3>Markers</h3>
        <div className="sidebar-header-actions">
          {selectedMarkers.size > 0 && (
            <span className="selected-count">{selectedMarkers.size} selected</span>
          )}
          {onClose && (
            <button type="button" className="sidebar-mobile-close" onClick={onClose} aria-label="Close">
              ×
            </button>
          )}
        </div>
      </div>

      {/* Search Panel */}
      <div className="filter-panel">
        <div className="filter-header" onClick={() => setSearchExpanded(!searchExpanded)}>
          <div className="filter-title">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z"/>
            </svg>
            <span>Search</span>
          </div>
          <div className="filter-summary">
            {searchTerm && (
              <span className="visible-count">{filteredMarkers.length} results</span>
            )}
            <span className={`expand-icon ${searchExpanded ? 'expanded' : ''}`}>▼</span>
          </div>
        </div>

        {searchExpanded && (
          <div className="filter-content">
            <div className="search-inputs">
              <input
                type="text"
                placeholder="Search markers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
              <select
                value={filterField}
                onChange={(e) => setFilterField(e.target.value)}
                className="filter-select"
              >
                <option value="all">All Fields</option>
                {viewableFields.map(field => (
                  <option key={field} value={field}>
                    {field}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Filter Panel */}
      <FilterPanel
        markers={markers}
        sheetConfig={sheetConfig}
        onFilterChange={handleFilterChange}
        onColorChange={onColorChange}
        onGroupVisibilityChange={onGroupVisibilityChange}
      />

      <div className="sidebar-content">
      {/* Bulk Edit Mode - Multiple Selected */}
      {selectedMarkers.size > 1 && (
        <div className="bulk-edit-section">
          <div className="section-header">
            <h4>Bulk Edit ({selectedMarkers.size} markers)</h4>
            <button onClick={handleDeselectAll} className="clear-btn">
              Clear Selection
            </button>
          </div>

          <div className="bulk-fields">
            {editableFields.map(field => (
              <div key={field} className="bulk-field">
                <label className="field-toggle">
                  <input
                    type="checkbox"
                    checked={enabledBulkFields[field] || false}
                    onChange={() => handleToggleBulkField(field)}
                  />
                  <span>Update {field.charAt(0).toUpperCase() + field.slice(1)}</span>
                </label>
                {enabledBulkFields[field] && (
                  <div className="field-input-wrapper">
                    {renderField(field, bulkFormData[field], handleBulkFieldChange)}
                  </div>
                )}
              </div>
            ))}
          </div>

          <button onClick={handleApplyBulk} className="apply-btn">
            Apply to {selectedMarkers.size} Markers
          </button>
        </div>
      )}

      {/* Single Edit Mode - One Selected */}
      {selectedMarkers.size === 1 && editingMarkerId && (
        <div className="single-edit-section">
          <div className="section-header">
            <h4>Edit Marker</h4>
            <button onClick={() => setEditingMarkerId(null)} className="close-btn">
              ×
            </button>
          </div>

          <form onSubmit={handleSaveSingle}>
            <div className="edit-fields">
              {editableFields.map(field => (
                <div key={field} className="edit-field">
                  <label className="field-label">
                    {field.charAt(0).toUpperCase() + field.slice(1)}
                  </label>
                  {renderField(field, formData[field], handleFieldChange)}
                </div>
              ))}
            </div>

            <div className="edit-actions">
              <button type="button" onClick={() => setEditingMarkerId(null)} className="btn-cancel">
                Cancel
              </button>
              <button type="submit" className="btn-save">
                Save Changes
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Search Results - Only show when searching */}
      {searchTerm && filteredMarkers.length > 0 && (
        <>
          <div className="selection-controls">
            <button onClick={handleSelectAll} className="select-btn">
              Select All ({filteredMarkers.length})
            </button>
            {selectedMarkers.size > 0 && (
              <button onClick={handleDeselectAll} className="select-btn">
                Deselect All
              </button>
            )}
          </div>

          <div className="marker-list">
            {filteredMarkers.map(marker => {
              const isSelected = selectedMarkers.has(marker.id);
              const isEditing = editingMarkerId === marker.id;
              const title = getMarkerTitle(marker);
              const description = marker.description || '';

              return (
                <div
                  key={marker.id}
                  className={`marker-item ${isSelected ? 'selected' : ''} ${isEditing ? 'editing' : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => handleToggleMarker(marker.id)}
                    className="marker-checkbox"
                  />
                  <div
                    className="marker-info"
                    onClick={() => handleMarkerClickInternal(marker.id)}
                  >
                    <div
                      className="marker-color-dot"
                      style={{ backgroundColor: marker.color || '#4285F4' }}
                    />
                    <div className="marker-details">
                      <div className="marker-title">{title}</div>
                      {/* Viewable Fields */}
                      {viewableFields.length > 0 && (
                        <div className="marker-viewable-fields">
                          {viewableFields.map(field => {
                            const value = marker[field];
                            if (!value || field === 'id' || field === 'rowIndex' || field === 'latitude' || field === 'longitude' || field === 'color') {
                              return null;
                            }
                            // Skip if field is used as title
                            const titleField = sheetConfig?.titleColumn?.toLowerCase();
                            const markerFieldKey = Object.keys(marker).find(key => key.toLowerCase() === titleField);
                            if (markerFieldKey === field) {
                              return null;
                            }
                            return (
                              <div key={field} className="viewable-field">
                                <span className="field-name">{field}:</span>
                                <span className="field-value">{String(value)}</span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Selected Markers List - Show when not searching */}
      {!searchTerm && selectedMarkers.size > 0 && (
        <div className="selected-markers-list">
          <div className="section-header" style={{ marginBottom: '15px' }}>
            <h4>Selected Markers ({selectedMarkers.size})</h4>
            <button onClick={handleDeselectAll} className="clear-btn">
              Clear All
            </button>
          </div>
          <div className="marker-list">
            {selectedMarkersArray.map(marker => {
              const isEditing = editingMarkerId === marker.id;
              const title = getMarkerTitle(marker);
              const description = marker.description || '';

              return (
                <div
                  key={marker.id}
                  className={`marker-item selected ${isEditing ? 'editing' : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={true}
                    onChange={() => handleToggleMarker(marker.id)}
                    className="marker-checkbox"
                  />
                  <div
                    className="marker-info"
                    onClick={() => handleMarkerClickInternal(marker.id)}
                  >
                    <div
                      className="marker-color-dot"
                      style={{ backgroundColor: marker.color || '#4285F4' }}
                    />
                    <div className="marker-details">
                      <div className="marker-title">{title}</div>
                      {/* Viewable Fields */}
                      {viewableFields.length > 0 && (
                        <div className="marker-viewable-fields">
                          {viewableFields.map(field => {
                            const value = marker[field];
                            if (!value || field === 'id' || field === 'rowIndex' || field === 'latitude' || field === 'longitude' || field === 'color') {
                              return null;
                            }
                            // Skip if field is used as title
                            const titleField = sheetConfig?.titleColumn?.toLowerCase();
                            const markerFieldKey = Object.keys(marker).find(key => key.toLowerCase() === titleField);
                            if (markerFieldKey === field) {
                              return null;
                            }
                            return (
                              <div key={field} className="viewable-field">
                                <span className="field-name">{field}:</span>
                                <span className="field-value">{String(value)}</span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Empty State */}
      {searchTerm && filteredMarkers.length === 0 && (
        <div className="empty-state">
          <p>No markers match your search</p>
        </div>
      )}

      {/* Default State - No search, no selection */}
      {!searchTerm && selectedMarkers.size === 0 && (
        <div className="empty-state">
          <p>Click markers on the map to select them</p>
          <p className="empty-hint">or use the search box to find specific markers</p>
        </div>
      )}
      </div>
    </div>
  );
}

export default Sidebar;
